import { Feature, type MapBrowserEvent } from 'ol';
import { type Geometry, Point } from 'ol/geom';
import { DragBox, Draw } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import type Map from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { useCallback, useEffect, useRef } from 'react';
import { useFillableSubmissionContext } from '../../../../FillableSubmissionContext.tsx';
import { type IMapWidget } from '../../IMapWidget.ts';
import { IDENTIFY_HIGHLIGHT_Z_INDEX } from '../../MapWidgetLogic.ts';
import { createHighlightStyle, geoJSONEpsg3857 } from '../../mapStyleUtils.ts';
import { getBufferedFeatureJSON } from '../intersection/utils/getBufferedFeatureJSON.ts';
import { type MapDrawingMode } from '../useMapDrawingInteractions.tsx';

/**
 * Modalità di selezione per l'interrogazione:
 * punto (con buffer), rettangolo (DragBox) o poligono libero.
 */
export type IdentifySelectionMode = 'point' | 'rectangle' | 'polygon';

/**
 * Opzioni per il hook {@link useMapIdentifyInteractions}.
 */
export interface IMapIdentifyInteractionsOptions {
  /** Riferimento alla mappa OpenLayers. */
  mapRef: React.RefObject<Map | null>;
  /** Modalità corrente del widget (view, draw, delete, identify). */
  mode: MapDrawingMode;
  /** Tipo di selezione identify attiva. */
  identifySelectionMode: IdentifySelectionMode;
  /** Widget mappa, per leggere la configurazione del buffer identify. */
  widget: IMapWidget;
  /**
   * Callback invocata al completamento della selezione,
   * con la geometria risultante (buffer, rettangolo o poligono).
   */
  onSelectionComplete: (geometry: Geometry) => void;
}

/** Stile per evidenziare l'area di selezione identify sulla mappa. */
const highlightStyle = createHighlightStyle(
  'rgba(0, 120, 255, 0.15)',
  'rgba(0, 120, 255, 0.8)',
);

/**
 * Gestisce le interazioni OL per la selezione identify.
 * Supporta tre modalità: punto (con buffer), rettangolo (DragBox)
 * e poligono (Draw).
 */
export function useMapIdentifyInteractions(
  options: IMapIdentifyInteractionsOptions,
) {
  const { mapRef, mode, identifySelectionMode, widget, onSelectionComplete } =
    options;
  const { isBackoffice } = useFillableSubmissionContext();

  /** Buffer puntuale: in Back Office usa il valore dedicato se configurato. */
  const identify = widget.identify;
  const standardBufferDistance = identify?.bufferDistance ?? 10;
  const bufferDistance = isBackoffice
    ? (identify?.bufferDistanceBO ?? standardBufferDistance)
    : standardBufferDistance;

  const highlightSource = useRef(new VectorSource({ wrapX: false }));
  const highlightLayerRef = useRef<VectorLayer<Feature<Geometry>> | null>(null);
  const dragBoxRef = useRef<DragBox | null>(null);
  const drawRef = useRef<Draw | null>(null);
  const clickListenerRef = useRef<((e: MapBrowserEvent<any>) => void) | null>(
    null,
  );

  /**
   * Rimuove le interazioni OL attive (DragBox, Draw, click)
   * e pulisce le feature di highlight. Non rimuove il layer
   * di highlight, che persiste tra i cambi di sub-modalità.
   */
  const cleanupInteractions = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (dragBoxRef.current) {
      map.removeInteraction(dragBoxRef.current);
      dragBoxRef.current = null;
    }
    if (drawRef.current) {
      map.removeInteraction(drawRef.current);
      drawRef.current = null;
    }
    if (clickListenerRef.current) {
      map.un('singleclick', clickListenerRef.current);
      clickListenerRef.current = null;
    }
    highlightSource.current.clear();
  }, [mapRef]);

  /**
   * Rimuove il layer vettoriale di highlight dalla mappa.
   * Chiamato solo all'uscita dalla modalità identify.
   */
  const removeHighlightLayer = useCallback(() => {
    const map = mapRef.current;
    if (!map || !highlightLayerRef.current) return;
    map.removeLayer(highlightLayerRef.current);
    highlightLayerRef.current = null;
  }, [mapRef]);

  /**
   * Crea e aggiunge il layer di highlight alla mappa,
   * se non già presente (idempotente).
   */
  const ensureHighlightLayer = useCallback(() => {
    const map = mapRef.current;
    if (!map || highlightLayerRef.current) return;

    const layer = new VectorLayer({
      source: highlightSource.current,
      style: highlightStyle,
    });
    layer.setZIndex(IDENTIFY_HIGHLIGHT_Z_INDEX);
    map.addLayer(layer);
    highlightLayerRef.current = layer;
  }, [mapRef]);

  useEffect(() => {
    const map = mapRef.current;

    if (mode !== 'identify' || !map) {
      cleanupInteractions();
      removeHighlightLayer();
      return;
    }

    cleanupInteractions();
    ensureHighlightLayer();

    switch (identifySelectionMode) {
      case 'point': {
        const handler = (e: MapBrowserEvent<any>) => {
          highlightSource.current.clear();
          const point = new Point(e.coordinate);
          const tempFeature = new Feature<Geometry>(point);
          const buffered = getBufferedFeatureJSON(tempFeature, bufferDistance);
          if (!buffered) return;

          const bufferedFeature = geoJSONEpsg3857.readFeature(buffered);
          highlightSource.current.addFeature(bufferedFeature);
          const geom = bufferedFeature.getGeometry();
          if (geom) onSelectionComplete(geom);
        };
        map.on('singleclick', handler);
        clickListenerRef.current = handler;
        break;
      }

      case 'rectangle': {
        const dragBox = new DragBox();
        dragBox.on('boxend', () => {
          highlightSource.current.clear();
          const geom = dragBox.getGeometry();
          const feature = new Feature<Geometry>(geom);
          highlightSource.current.addFeature(feature);
          onSelectionComplete(geom);
        });
        map.addInteraction(dragBox);
        dragBoxRef.current = dragBox;
        break;
      }

      case 'polygon': {
        const draw = new Draw({
          type: 'Polygon',
        });
        draw.on('drawend', e => {
          highlightSource.current.clear();
          const geom = e.feature.getGeometry();
          if (geom) {
            highlightSource.current.addFeature(e.feature);
            onSelectionComplete(geom);
          }
        });
        map.addInteraction(draw);
        drawRef.current = draw;
        break;
      }
    }

    return () => {
      cleanupInteractions();
      removeHighlightLayer();
    };
  }, [
    mode,
    identifySelectionMode,
    mapRef,
    bufferDistance,
    onSelectionComplete,
    cleanupInteractions,
    removeHighlightLayer,
    ensureHighlightLayer,
  ]);

  /** Rimuove la selezione sulla mappa, lasciando attive le interazioni. */
  const clearSelection = useCallback(() => {
    highlightSource.current.clear();
  }, []);

  return { clearSelection };
}
